/**
 * DL-01 — the download run: the manifests of ONE scope written under the
 * folder the user picked (story §B).
 *
 *   1. VERIFY every data set first (`verify.ts`): the folder
 *      `<root>/<folderName>/` is created, every manifest entry compared with
 *      the file of that name on disk — size, then MD5 — so the "required
 *      bytes" are known before the first write and a second run never
 *      downloads twice;
 *   2. DOWNLOAD one data set at a time (progress per DA), ×4 files at a time
 *      (`pool.ts`): `fetch(url)` STREAMED into `createWritable()` — the MD5
 *      runs over the chunks as they are written and is checked against the
 *      manifest, the size too; a wrong copy is discarded and reported;
 *   3. a 403 / 401 on a file = the SAS bucket expired → the manifests are
 *      re-requested ONCE for the run and the file retried with its new URL
 *      (§A-6; the client never caches URLs);
 *   4. a WRITE failure (`QuotaExceededError` = disk full, or any other) stops
 *      the run cleanly: no new file starts, the files in flight finish, the
 *      partial file is discarded, the progress is kept — the dialog says
 *      "Disk full after X of N files (Y written) — free space and press
 *      Resume"; Resume re-verifies and continues (step 1 again);
 *   5. `manifest.json` (ids, md5s, the scope ids — `folder.ts`) is written
 *      into a data set's folder once its files are all on disk.
 *
 * For a platform admin (DL-04, `deps.fetchBundle` set) every data set's
 * `bundle.json` 2.0 — lyse/v1 `getExportBundle` — is written FIRST, right
 * after the verify pass and before the first image (Christian 2026-10-26: the
 * structure is on disk even when the images are stopped or cut). A failed
 * bundle fetch / write is reported like a failed `manifest.json` and never
 * fails the images; a Resume writes it again (fresh).
 *
 * Never throws for a file: every failure is a named result the progress
 * carries. The deps are injected (fetch, the manifest re-request, the clock)
 * so the whole run is testable against an in-memory folder.
 */
import {
  MANIFEST_FILE,
  buildFolderManifest,
  safeSegment,
  serializeFolderManifest,
} from "./folder.js";
import {
  discardFile,
  existingFileOf,
  type DownloadDirLike,
  type WritableLike,
} from "./fs.js";
import type {
  DownloadManifest,
  DownloadScope,
  ManifestImage,
} from "./manifestApi.js";
import { Md5Stream, md5OfBlob } from "./md5.js";
import { runPool } from "./pool.js";
import {
  summarizeVerification,
  verifyEntry,
  type VerifyVerdict,
} from "./verify.js";

export const DOWNLOAD_CONCURRENCY = 4;

/** DL-04 — the export bundle's file name beside `manifest.json`. */
export const BUNDLE_FILE = "bundle.json";

export type DaState = "pending" | "verifying" | "running" | "done" | "stopped";

export interface DaProgress {
  daId: string;
  folderName: string;
  /** files the manifest lists */
  total: number;
  /** files on disk and proved (downloaded now, or already present) */
  done: number;
  presentMd5: number;
  presentSize: number;
  failed: number;
  bytesWritten: number;
  /** bytes still to fetch after the verify pass */
  requiredBytes: number;
  state: DaState;
}

export type StopReason =
  | { kind: "disk_full"; fileName: string; detail: string | null }
  | { kind: "write_failed"; fileName: string; detail: string | null }
  | { kind: "stopped" };

export type FileError =
  | "fetch_failed"
  | "expired"
  | "size_mismatch"
  | "md5_mismatch"
  | "manifest_write_failed"
  | "bundle_write_failed";

export interface FileFailure {
  daId: string;
  fileName: string;
  error: FileError;
  detail?: string | undefined;
}

export interface RunProgress {
  phase: "verifying" | "running" | "done" | "stopped";
  das: DaProgress[];
  filesTotal: number;
  /** downloaded now + already present, across the data sets */
  filesDone: number;
  filesPresent: number;
  bytesWritten: number;
  requiredBytes: number;
  /** the verify pass over every data set is complete */
  verified: boolean;
  stop: StopReason | null;
  failures: FileFailure[];
  /** the SAS links expired during the run and were renewed once */
  linksRenewed: boolean;
}

export interface RunDeps {
  /** `fetch(url)` of a SAS URL — no credentials, no auth header. */
  fetchBytes: (url: string) => Promise<Response>;
  /** ONE data set's manifest re-requested (the expired-bucket retry, §A-6). */
  refreshManifest: (dataAcquisitionId: string) => Promise<DownloadManifest>;
  onProgress: (p: RunProgress) => void;
  /**
   * DL-04 — the data set's `bundle.json` 2.0 (platform admin only; absent =
   * no bundle is written). Called once per data set after its files.
   */
  fetchBundle?: ((dataAcquisitionId: string) => Promise<unknown>) | undefined;
  /** Read before every file start — the user's Stop. */
  shouldStop: () => boolean;
  concurrency?: number | undefined;
  now?: (() => Date) | undefined;
}

/** The default byte reader: a plain cross-origin GET, nothing of ours attached. */
export function fetchSasBytes(url: string): Promise<Response> {
  return fetch(url, { method: "GET", credentials: "omit", cache: "no-store" });
}

// ── one file ───────────────────────────────────────────────────────────────

type OneResult =
  | { kind: "ok"; bytes: number }
  | { kind: "expired" }
  | { kind: "fetch_failed"; detail: string }
  | { kind: "size_mismatch"; detail: string }
  | { kind: "md5_mismatch"; detail: string }
  | { kind: "disk_full"; detail: string | null }
  | { kind: "write_failed"; detail: string | null };

class ReadError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "ReadError";
  }
}

/** `message` / `name` read by shape: a `DOMException` of another realm (a
 *  worker, jsdom) is not `instanceof` this realm's `Error`. */
function detailOf(e: unknown): string | null {
  if (typeof e === "string") return e !== "" ? e : null;
  const m = (e as { message?: unknown } | null)?.message;
  return typeof m === "string" && m !== "" ? m : null;
}

function errorNameOf(e: unknown): string {
  const n = (e as { name?: unknown } | null)?.name;
  return typeof n === "string" ? n : "";
}

function writeFailure(e: unknown): OneResult {
  const name = errorNameOf(e);
  return name === "QuotaExceededError"
    ? { kind: "disk_full", detail: detailOf(e) }
    : { kind: "write_failed", detail: detailOf(e) };
}

/** The file's name on disk: as served, path separators stripped (`folder.ts`). */
export function nameOnDisk(
  image: Pick<ManifestImage, "fileName" | "imageId">,
): string {
  return safeSegment(
    image.fileName,
    image.imageId !== "" ? image.imageId : "image",
  );
}

/** Streams `url` into `<dir>/<image.fileName>`, proving size and MD5. */
export async function downloadOne(
  dir: DownloadDirLike,
  image: ManifestImage,
  url: string,
  fetchBytes: RunDeps["fetchBytes"],
): Promise<OneResult> {
  const name = nameOnDisk(image);
  let res: Response;
  try {
    res = await fetchBytes(url);
  } catch (e) {
    return { kind: "fetch_failed", detail: detailOf(e) ?? "network error" };
  }
  if (res.status === 403 || res.status === 401) return { kind: "expired" };
  if (!res.ok)
    return { kind: "fetch_failed", detail: `HTTP ${String(res.status)}` };

  let w: WritableLike;
  try {
    const handle = await dir.getFileHandle(name, { create: true });
    w = await handle.createWritable();
  } catch (e) {
    return writeFailure(e);
  }
  const md5 = new Md5Stream();
  let written = 0;
  try {
    const body = res.body;
    if (body) {
      const reader = body.getReader();
      for (;;) {
        let step: ReadableStreamReadResult<Uint8Array>;
        try {
          step = await reader.read();
        } catch (e) {
          throw new ReadError(e);
        }
        if (step.done) break;
        const chunk = step.value;
        await w.write(chunk);
        md5.append(chunk);
        written += chunk.byteLength;
      }
    } else {
      let buf: Uint8Array;
      try {
        buf = new Uint8Array(await res.arrayBuffer());
      } catch (e) {
        throw new ReadError(e);
      }
      await w.write(buf);
      md5.append(buf);
      written += buf.byteLength;
    }
    await w.close();
  } catch (e) {
    await w.abort?.().catch(() => undefined);
    await discardFile(dir, name);
    if (e instanceof ReadError)
      return { kind: "fetch_failed", detail: e.message };
    return writeFailure(e);
  }
  if (image.bytes > 0 && written !== image.bytes) {
    await discardFile(dir, name);
    return {
      kind: "size_mismatch",
      detail: `${String(written)} bytes received, ${String(image.bytes)} expected`,
    };
  }
  const digest = md5.end();
  if (image.md5 !== null && digest !== image.md5) {
    await discardFile(dir, name);
    return { kind: "md5_mismatch", detail: "the bytes received differ" };
  }
  return { kind: "ok", bytes: written };
}

async function writeText(
  dir: DownloadDirLike,
  name: string,
  text: string,
): Promise<void> {
  const h = await dir.getFileHandle(name, { create: true });
  const w = await h.createWritable();
  try {
    await w.write(new TextEncoder().encode(text));
    await w.close();
  } catch (e) {
    await w.abort?.().catch(() => undefined);
    throw e;
  }
}

// ── the run ────────────────────────────────────────────────────────────────

interface DaPlan {
  manifest: DownloadManifest;
  dir: DownloadDirLike;
  toDownload: ManifestImage[];
}

export async function runImageDownload(
  root: DownloadDirLike,
  scope: DownloadScope,
  manifests: readonly DownloadManifest[],
  deps: RunDeps,
): Promise<RunProgress> {
  const now = deps.now ?? (() => new Date());
  const concurrency = deps.concurrency ?? DOWNLOAD_CONCURRENCY;
  const progress: RunProgress = {
    phase: "verifying",
    das: manifests.map((m) => ({
      daId: m.dataAcquisitionId,
      folderName: safeSegment(m.folderName),
      total: m.images.length,
      done: 0,
      presentMd5: 0,
      presentSize: 0,
      failed: 0,
      bytesWritten: 0,
      requiredBytes: 0,
      state: "pending",
    })),
    filesTotal: manifests.reduce((n, m) => n + m.images.length, 0),
    filesDone: 0,
    filesPresent: 0,
    bytesWritten: 0,
    requiredBytes: 0,
    verified: false,
    stop: null,
    failures: [],
    linksRenewed: false,
  };
  const emit = (): void => {
    deps.onProgress({
      ...progress,
      das: progress.das.map((d) => ({ ...d })),
      failures: [...progress.failures],
    });
  };
  emit();

  // 1 · verify every data set (the required bytes before the first write)
  const plans: DaPlan[] = [];
  for (let k = 0; k < manifests.length; k++) {
    const m = manifests[k];
    const da = progress.das[k];
    if (!m || !da) continue;
    da.state = "verifying";
    emit();
    const dir = await root.getDirectoryHandle(da.folderName, { create: true });
    const verdicts: VerifyVerdict[] = [];
    for (const image of m.images) {
      const existing = await existingFileOf(dir, nameOnDisk(image));
      verdicts.push(
        await verifyEntry(
          image,
          existing
            ? { size: existing.size, md5: () => md5OfBlob(existing) }
            : null,
        ),
      );
    }
    const s = summarizeVerification(m.images, verdicts);
    da.presentMd5 = s.presentMd5;
    da.presentSize = s.presentSize;
    da.done = s.presentMd5 + s.presentSize;
    da.requiredBytes = s.requiredBytes;
    da.state = "pending";
    progress.filesPresent += da.done;
    progress.filesDone += da.done;
    progress.requiredBytes += s.requiredBytes;
    plans.push({
      manifest: m,
      dir,
      toDownload: m.images.filter(
        (_i, j) => verdicts[j]?.outcome === "download",
      ),
    });
    emit();
  }
  progress.verified = true;
  progress.phase = "running";
  emit();

  // DL-04 · bundle.json FIRST, one per data set folder (platform admin)
  if (deps.fetchBundle) {
    for (const plan of plans) {
      if (deps.shouldStop()) break;
      const daId = plan.manifest.dataAcquisitionId;
      try {
        const bundle = await deps.fetchBundle(daId);
        await writeText(
          plan.dir,
          BUNDLE_FILE,
          `${JSON.stringify(bundle, null, 2)}\n`,
        );
      } catch (e) {
        progress.failures.push({
          daId,
          fileName: BUNDLE_FILE,
          error: "bundle_write_failed",
          detail: detailOf(e) ?? undefined,
        });
        emit();
      }
    }
  }

  // 2 · download, one data set at a time, ×concurrency files
  const halt = { stopped: false };
  const halted = (): boolean => halt.stopped;
  // the expired-bucket renewal: ONE re-request per data set per run (its
  // `dataAcquisitionId`), shared by the files in flight; a renewed URL is
  // found by imageId, else by file name
  const renewed = new Map<string, Promise<Map<string, string>>>();
  const renewedUrlOf = (
    daId: string,
    image: ManifestImage,
  ): Promise<string | null> => {
    let p = renewed.get(daId);
    if (!p) {
      p = deps
        .refreshManifest(daId)
        .then((rm) => {
          const urls = new Map<string, string>();
          for (const i of rm.images) {
            if (i.imageId !== "") urls.set(`id:${i.imageId}`, i.url);
            urls.set(`name:${i.fileName}`, i.url);
          }
          progress.linksRenewed = true;
          return urls;
        })
        .catch(() => new Map<string, string>());
      renewed.set(daId, p);
    }
    return p.then(
      (urls) =>
        (image.imageId !== "" ? urls.get(`id:${image.imageId}`) : undefined) ??
        urls.get(`name:${image.fileName}`) ??
        null,
    );
  };

  for (let k = 0; k < plans.length; k++) {
    const plan = plans[k];
    const da = progress.das[k];
    if (!plan || !da) continue;
    if (halted() || deps.shouldStop()) {
      halt.stopped = true;
      progress.stop ??= { kind: "stopped" };
      break;
    }
    da.state = "running";
    emit();
    await runPool(
      plan.toDownload,
      concurrency,
      async (image) => {
        let r = await downloadOne(plan.dir, image, image.url, deps.fetchBytes);
        if (r.kind === "expired") {
          const url = await renewedUrlOf(da.daId, image);
          if (url !== null && url !== image.url)
            r = await downloadOne(plan.dir, image, url, deps.fetchBytes);
        }
        if (r.kind === "ok") {
          da.done++;
          da.bytesWritten += r.bytes;
          progress.filesDone++;
          progress.bytesWritten += r.bytes;
        } else if (r.kind === "disk_full" || r.kind === "write_failed") {
          progress.stop ??= {
            kind: r.kind,
            fileName: image.fileName,
            detail: r.detail,
          };
          halt.stopped = true;
        } else {
          da.failed++;
          progress.failures.push({
            daId: da.daId,
            fileName: image.fileName,
            error: r.kind,
            ...(r.kind === "expired" ? {} : { detail: r.detail }),
          });
        }
        emit();
      },
      () => halted() || deps.shouldStop(),
    );
    if (!halted() && deps.shouldStop()) {
      halt.stopped = true;
      progress.stop ??= { kind: "stopped" };
    }
    if (halted()) {
      da.state = "stopped";
      break;
    }
    // 5 · manifest.json beside the files
    try {
      await writeText(
        plan.dir,
        MANIFEST_FILE,
        serializeFolderManifest(
          buildFolderManifest(scope, plan.manifest, now()),
        ),
      );
    } catch (e) {
      progress.failures.push({
        daId: da.daId,
        fileName: MANIFEST_FILE,
        error: "manifest_write_failed",
        detail: detailOf(e) ?? undefined,
      });
    }
    da.state = "done";
    emit();
  }

  progress.phase = halted() ? "stopped" : "done";
  emit();
  return progress;
}
